<template>
    <span>
        <template v-if="page == 'select_house'">
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <div class="form-group">
                            <label>Please select house</label>
                            <div class="input-group align-items-center">
                                <select class="form-control" v-model="selectedHouse" @change="onHouseChange">
                                    <option :value="null">Please Select</option>
                                    <option v-for="house in houses" :key="house.slug" :value="house">{{ house.label }}</option>
                                </select>
                            </div>
                            <small v-if="housesLoaded && houses.length === 0" class="form-text text-muted">
                                You haven't added a house yet. <a :href="route('owner.houses.create')">Add your house</a> to start.
                            </small>
                        </div>
                    </div>
                </div>
                <div class="row mt-5" v-if="selectedHouse == null">
                    <div class="col-sm-12 d-flex justify-content-end">
                        <img src="/img/graphics/form-bg.png" class="img-fluid form-img">
                    </div>
                </div>

                <template v-else>
                    <!-- Modern Indigo/Slate Brand Banner -->
                    <div class="row mt-3">
                        <div class="col-md-12 col-sm-12">
                            <div class="card house-banner-card border-0 text-white shadow-sm overflow-hidden position-relative">
                                <div class="banner-bg-decoration"></div>
                                <div class="card-body p-3 p-md-4 position-relative z-index-1">
                                    <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
                                        <!-- Left side: House Icon Avatar & Name -->
                                        <div class="d-flex align-items-center mb-3 mb-md-0">
                                            <div class="banner-icon-badge mr-3 flex-shrink-0 d-flex align-items-center justify-content-center">
                                                <i class="las la-home la-2x text-white"></i>
                                            </div>
                                            <div>
                                                <div class="d-flex align-items-center flex-wrap">
                                                    <h5 class="font-weight-bold mb-0 text-white mr-2">{{ selectedHouse.label }}</h5>
                                                    <span class="badge badge-pill badge-house-type px-2 py-1 mt-1 mt-sm-0" v-if="selectedHouse.house_type">
                                                        {{ selectedHouse.house_type }}
                                                    </span>
                                                </div>
                                                <span class="text-sm text-white-75 mt-1 d-block">
                                                    {{ groupedAssessments.length }} {{ groupedAssessments.length === 1 ? 'room' : 'rooms' }}
                                                    · {{ assessments.length }} {{ assessments.length === 1 ? 'part' : 'parts' }} checked
                                                    · {{ totalDefects }} {{ totalDefects === 1 ? 'defect' : 'defects' }}
                                                </span>
                                                <!-- <span class="text-xs text-white-75 mt-1 d-block">
                                                    <i class="las la-clipboard-check mr-1"></i>House Assessment Profile
                                                </span> -->
                                            </div>
                                        </div>

                                        <!-- Right side: Metadata Chips -->
                                        <div class="banner-meta-grid d-flex flex-column flex-md-row">
                                            <div class="meta-chip py-2 px-3 bg-white-10 d-flex align-items-center" v-if="selectedHouse.information && selectedHouse.information.local_authority">
                                                <i class="las la-building text-info mr-2 la-lg flex-shrink-0"></i>
                                                <div class="min-w-0 flex-grow-1">
                                                    <div class="text-micro text-white-50 text-uppercase">Authority</div>
                                                    <div class="meta-chip-value font-weight-semibold text-white">{{ selectedHouse.information.local_authority }}</div>
                                                </div>
                                            </div>

                                            <div class="meta-chip py-2 px-3 bg-white-10 d-flex align-items-center" v-if="selectedHouse.address && selectedHouse.address.state">
                                                <i class="las la-map-marker-alt text-warning mr-2 la-lg flex-shrink-0"></i>
                                                <div class="min-w-0 flex-grow-1">
                                                    <div class="text-micro text-white-50 text-uppercase">State</div>
                                                    <div class="meta-chip-value font-weight-semibold text-white">{{ selectedHouse.address.state }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- END: Modern Banner -->

                    <!-- Unfinished inspection saved on this device -->
                    <div v-if="draft" class="alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center mt-3 mb-0">
                        <div class="flex-grow-1 mb-2 mb-sm-0">
                            <i class="las la-pause-circle la-lg mr-1"></i>
                            <strong>Unfinished inspection:</strong> {{ draftDescription }}
                        </div>
                        <div class="d-flex">
                            <button type="button" class="btn btn-primary btn-sm mr-2 flex-fill" @click="continueDraft">Continue</button>
                            <button type="button" class="btn btn-outline-secondary btn-sm flex-fill" @click="discardDraft">Discard</button>
                        </div>
                    </div>

                    <!-- Grouped Area Inspections -->
                    <div class="mt-4" v-if="groupedAssessments.length > 0">
                        <h5 class="mb-3 text-secondary font-weight-bold">Inspection Areas</h5>

                        <div
                            v-for="group in groupedAssessments"
                            :key="group.key"
                            class="card border shadow-sm mb-4 rounded-lg area-group-card"
                        >
                            <!-- Area Header -->
                            <div
                                class="card-header bg-light py-3 px-3 d-flex align-items-center justify-content-between cursor-pointer"
                                @click="toggleArea(group.key)"
                            >
                                <div class="d-flex align-items-center flex-wrap">
                                    <span class="area-icon-wrapper mr-2 d-inline-flex align-items-center justify-content-center bg-white rounded-circle border p-2">
                                        <i :class="'las la-' + (group.area && group.area.icon ? group.area.icon : 'home') + ' la-lg text-primary'"></i>
                                    </span>
                                    <h6 class="mb-0 font-weight-bold text-dark mr-2">{{ group.label }}</h6>
                                    <a
                                        v-if="group.areaInstanceId"
                                        href="#"
                                        class="text-muted mr-2"
                                        title="Rename this area"
                                        @click.stop.prevent="renameArea(group)"
                                    >
                                        <i class="las la-pen"></i>
                                    </a>
                                    <span class="badge badge-primary badge-pill px-2 py-1">
                                        {{ group.items.length }} {{ group.items.length === 1 ? 'Component' : 'Components' }}
                                    </span>
                                </div>
                                <div class="d-flex align-items-center">
                                    <i :class="isCollapsed(group.key) ? 'las la-angle-down la-lg' : 'las la-angle-up la-lg'"></i>
                                </div>
                            </div>

                            <!-- Area Components List -->
                            <div v-show="!isCollapsed(group.key)" class="card-body p-3">
                                <div class="row">
                                    <div
                                        v-for="assessment in group.items"
                                        :key="assessment.id"
                                        class="col-md-6 col-sm-12 mb-3"
                                    >
                                        <div class="inspection-card border rounded p-3 d-flex flex-column bg-white shadow-xs">
                                            <div>
                                                <div class="d-flex justify-content-between align-items-start">
                                                    <p class="font-weight-bold text-dark mb-0">
                                                        {{ assessment.label_house_component || (assessment.component ? assessment.component.name : 'Component Inspection') }}
                                                    </p>
                                                    <span class="badge badge-info small ml-2 flex-shrink-0" v-if="assessment.defects">
                                                        <i class="las la-bug mr-1"></i>{{ assessment.defects.length }} {{ assessment.defects.length === 1 ? 'defect' : 'defects' }}
                                                    </span>
                                                </div>
                                                <p class="text-xs text-uppercase text-muted mb-2" v-if="assessment.component">
                                                    {{ assessment.component.name }}
                                                </p>
                                            </div>
                                            <div class="card-actions d-flex align-items-center mt-3 pt-2 border-top">
                                                <button type="button" @click="viewAssessment(assessment.id)" class="btn btn-primary btn-sm flex-fill mr-2">
                                                    <i class="las la-eye mr-1"></i>View
                                                </button>
                                                <button type="button" @click="deleteAssessment(assessment.id)" class="btn btn-outline-danger btn-sm btn-icon-square" title="Delete inspection" aria-label="Delete inspection">
                                                    <i class="las la-trash-alt"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Add Component to this Area button -->
                                <div class="mt-2 pt-2 border-top">
                                    <button
                                        type="button"
                                        @click="addComponentToArea(group)"
                                        class="btn btn-outline-primary btn-block py-2 font-weight-medium d-flex align-items-center justify-content-center"
                                    >
                                        <i class="las la-plus-circle la-lg mr-2"></i>Check another part of {{ group.label }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- empty state for a house with no inspections yet -->
                    <div v-if="assessmentsLoaded && assessments.length === 0" class="text-center text-muted mt-4">
                        <i class="las la-clipboard-list la-3x"></i>
                        <p class="mb-0 mt-2">No inspections yet. Start with any room, e.g. the living room.</p>
                    </div>

                    <!-- add new area inspection -->
                    <div class="row mt-3">
                        <div class="col-md-12 col-sm-12 align-items-center">
                            <button type="button" @click="addNewArea" class="btn btn-block p-0 border-0 text-decoration-none">
                                <div class="add-inspection d-flex align-items-center justify-content-center p-4 rounded border border-dashed bg-light">
                                    <span class="mb-0 font-weight-bold text-primary"><i class="las la-plus-circle la-lg mr-2"></i>{{ assessments.length ? 'Inspect another room' : 'Start inspecting a room' }}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="page == 'wizard'">
            <assessment-wizard
                :key="wizardKey"
                :house-id="selectedHouse"
                :initial-area="targetArea"
                :initial-area-label="targetAreaLabel"
                :initial-area-other="targetAreaOther"
                :initial-area-instance-id="targetAreaInstanceId"
                :resume-draft="resumeDraft"
                :route-step="routeStep"
                @step-change="onWizardStepChange"
                @step-back="onWizardStepBack"
                @add-another="addAnotherInArea"
                @cancel-wizard="leaveSubpage"
                @complete-wizard="leaveSubpage"
            ></assessment-wizard>
        </template>

        <template v-if="page == 'view'">
            <assessment-view :assessment-id="currentAssessmentId" @go-back="leaveSubpage"></assessment-view>
        </template>
    </span>
</template>

<script>
import AssessmentWizard from './AssessmentWizard.vue';
import { loadDraft, clearDraft, describeDraft } from '../../utils/assessmentDraft';

/**
 * The house view, wizard and inspection detail are one page. Each gets its
 * own URL hash (#inspect/2, #inspection/15) so the phone's back button,
 * refresh and swipe-back behave as owners expect.
 */
function parseHash(hash) {
    let match = /^#inspect(?:\/(\d))?$/.exec(hash);
    if (match) {
        return { page: 'wizard', step: match[1] ? Number(match[1]) : 1 };
    }
    match = /^#inspection\/(\d+)$/.exec(hash);
    if (match) {
        return { page: 'view', assessmentId: Number(match[1]) };
    }
    return { page: 'select_house' };
}

export default {
    components: { AssessmentWizard },
    data(){
        return {
            selectedHouse: null,
            initSelected: null,
            houses: [],
            housesLoaded: false,
            assessments: [],
            assessmentsLoaded: false,
            page: 'select_house',
            currentAssessmentId: null,
            targetArea: null,
            targetAreaLabel: null,
            targetAreaOther: null,
            targetAreaInstanceId: null,
            collapsed: {},
            draft: null,
            wizardKey: 0,
            resumeDraft: false,
            routeStep: null,
        };
    },
    computed: {
        groupedAssessments() {
            const groups = {};
            this.assessments.forEach(item => {
                // Group by the normalized area instance id so grouping stays correct
                // even if the label text ever differs across rows, and updates
                // immediately after a rename. Falls back to a text-based key only
                // for any legacy row that predates the area instance backfill.
                const key = item.house_area_instance_id
                    ? 'instance-' + item.house_area_instance_id
                    : 'legacy-' + (item.label_house_area || (item.area ? item.area.name : 'unassigned'));
                if (!groups[key]) {
                    groups[key] = {
                        key: key,
                        label: item.label_house_area || (item.area ? item.area.name : 'Unassigned Area'),
                        area: item.area || null,
                        areaOther: item.area_instance ? item.area_instance.house_area_other : null,
                        areaInstanceId: item.house_area_instance_id || null,
                        items: []
                    };
                }
                groups[key].items.push(item);
            });
            return Object.values(groups);
        },
        totalDefects() {
            return this.assessments.reduce((sum, item) => sum + (item.defects ? item.defects.length : 0), 0);
        },
        draftDescription() {
            return describeDraft(this.draft);
        },
    },
    mounted() {
        window.addEventListener('popstate', this.onPopState);
        this.loadHouses();
    },
    beforeDestroy() {
        window.removeEventListener('popstate', this.onPopState);
    },
    methods:{
        toggleArea(label) {
            this.$set(this.collapsed, label, !this.collapsed[label]);
        },
        isCollapsed(label) {
            return !!this.collapsed[label];
        },
        async loadHouses(){
            try {
                const { data } = await axios.get(route("owner.houses.list"));
                this.houses = data.data;
                if(this.initSelected){
                    this.selectedHouse = this.houses.find(house => house.slug === this.initSelected.slug) || this.initSelected;
                    this.loadAssessments();
                }
            } catch (e) {
                this.$swal('Error!', 'Could not load your houses.', 'error');
            } finally {
                this.housesLoaded = true;
                this.refreshDraft();
                // Restore the sub-page in the URL (e.g. after a refresh mid-inspection).
                window.history.replaceState(this.historyState(0), '', window.location.href);
                this.applyRoute();
            }
        },
        initSelectHouse(house){
            this.initSelected = house;
        },
        onHouseChange(){
            this.assessments = [];
            this.assessmentsLoaded = false;
            const url = new URL(window.location.href);
            if (this.selectedHouse) {
                url.searchParams.set('house', this.selectedHouse.slug);
            } else {
                url.searchParams.delete('house');
            }
            url.searchParams.delete('house_id');
            window.history.replaceState(this.historyState(0), '', url.pathname + url.search);
            this.refreshDraft();
            this.loadAssessments();
        },
        async loadAssessments(){
            if(this.selectedHouse){
                try {
                    const { data } = await axios.get(route("owner.houses.assessments", this.selectedHouse));
                    this.assessments = data;
                } catch (e) {
                    this.$swal('Error!', 'Could not load inspections.', 'error');
                } finally {
                    this.assessmentsLoaded = true;
                }
            }
        },
        refreshDraft(){
            this.draft = this.selectedHouse ? loadDraft(this.selectedHouse.slug) : null;
        },
        async deleteAssessment(id){
            this.$swal({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await axios.delete(route("owner.assessments.destroy", id ));
                        this.$swal({
                            title: 'Deleted!',
                            text: 'Assessment has been deleted.',
                            icon: 'success',
                            didClose: () =>{
                                this.loadAssessments();
                            }
                        })
                    } catch (e) {
                        this.$swal('Error!', 'Could not delete this inspection.', 'error');
                    }
                }
            });
        },

        /* ---------- navigation ---------- */

        historyDepth() {
            return (window.history.state && window.history.state.sqDepth) || 0;
        },
        historyState(depth, extra = {}) {
            return Object.assign({ sqDepth: depth }, extra);
        },
        navigate(hash, { replace = false, extra = {} } = {}){
            const url = window.location.pathname + window.location.search + hash;
            const depth = this.historyDepth();
            if (replace) {
                window.history.replaceState(this.historyState(depth, extra), '', url);
            } else {
                window.history.pushState(this.historyState(depth + 1, extra), '', url);
            }
            this.applyRoute();
        },
        onPopState(){
            this.applyRoute();
        },
        applyRoute(){
            const target = parseHash(window.location.hash);

            if (target.page === 'wizard') {
                if (!this.selectedHouse) {
                    this.navigate('', { replace: true });
                    return;
                }
                if (this.page !== 'wizard') {
                    // Arrived by refresh or the forward button: only a saved draft can be resumed.
                    if (!loadDraft(this.selectedHouse.slug)) {
                        this.navigate('', { replace: true });
                        return;
                    }
                    this.openWizard({ resume: true });
                }
                this.routeStep = target.step;
                return;
            }

            if (target.page === 'view') {
                this.currentAssessmentId = target.assessmentId;
                this.page = 'view';
                return;
            }

            if (this.page !== 'select_house') {
                this.page = 'select_house';
                this.currentAssessmentId = null;
                this.loadAssessments();
            }
            this.refreshDraft();
        },
        openWizard({ resume = false, area = null, areaOther = null, label = null, areaInstanceId = null } = {}){
            this.resumeDraft = resume;
            this.targetArea = area;
            this.targetAreaOther = areaOther;
            this.targetAreaLabel = label;
            this.targetAreaInstanceId = areaInstanceId;
            this.routeStep = null;
            this.wizardKey++;
            this.page = 'wizard';
        },
        startWizard(options){
            this.openWizard(options);
            this.navigate('#inspect', { extra: { step: 1 } });
        },
        onWizardStepChange({ step, replace }){
            const current = parseHash(window.location.hash);
            if (current.page === 'wizard' && current.step === step) {
                return;
            }
            this.navigate('#inspect/' + step, {
                replace,
                extra: { step, prevStep: replace ? (window.history.state || {}).prevStep : current.step },
            });
        },
        onWizardStepBack(step){
            if ((window.history.state || {}).prevStep === step) {
                window.history.back();
            } else {
                this.navigate('#inspect/' + step, { replace: true, extra: { step } });
            }
        },
        leaveSubpage(){
            const depth = this.historyDepth();
            if (depth > 0) {
                window.history.go(-depth);
            } else {
                this.navigate('', { replace: true });
            }
        },

        /* ---------- actions ---------- */

        addComponentToArea(group) {
            this.startWizard({ area: group.area, areaOther: group.areaOther, label: group.label, areaInstanceId: group.areaInstanceId });
        },
        async addNewArea() {
            if (this.draft) {
                const result = await this.$swal({
                    title: 'Continue where you left off?',
                    text: 'You have an unfinished inspection: ' + this.draftDescription,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#3EC0AD',
                    cancelButtonColor: '#3F3D56',
                    confirmButtonText: 'Continue it',
                    cancelButtonText: 'Start new',
                });
                if (result.isConfirmed) {
                    this.continueDraft();
                    return;
                }
                if (result.dismiss !== 'cancel') {
                    return;
                }
                this.discardDraft();
            }
            this.startWizard();
        },
        addAnotherInArea({ area, areaOther, label, areaInstanceId }){
            this.loadAssessments();
            this.openWizard({ area, areaOther, label, areaInstanceId });
        },
        continueDraft(){
            this.startWizard({ resume: true });
        },
        discardDraft(){
            if (this.selectedHouse) {
                clearDraft(this.selectedHouse.slug);
            }
            this.draft = null;
        },
        async renameArea(group){
            const { value: name } = await this.$swal({
                title: 'Rename area',
                input: 'text',
                inputValue: group.label,
                showCancelButton: true,
                confirmButtonColor: '#3EC0AD',
                cancelButtonColor: '#3F3D56',
                confirmButtonText: 'Save',
                inputValidator: (val) => !val ? 'Please enter a name' : null,
            });

            if (!name || name === group.label) {
                return;
            }

            try {
                await axios.put(route('owner.assessments.areaInstances.rename', group.areaInstanceId), { name });
                this.loadAssessments();
            } catch (e) {
                const message = e.response && e.response.data && e.response.data.message
                    ? e.response.data.message
                    : 'Could not rename this area.';
                this.$swal('Error!', message, 'error');
            }
        },
        viewAssessment(id){
            this.navigate('#inspection/' + id);
        },
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.house-banner-card {
    background: linear-gradient(135deg, #1e1b4b 0%, #2f2b5b 50%, #4338ca 100%);
    border-radius: 14px;
    box-shadow: 0 10px 25px -5px rgba(30, 27, 75, 0.3), 0 8px 10px -6px rgba(30, 27, 75, 0.2) !important;
}

.banner-bg-decoration {
    position: absolute;
    top: -50%;
    right: -10%;
    width: 250px;
    height: 250px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 70%);
    border-radius: 50%;
    pointer-events: none;
}

.banner-icon-badge {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 12px;
}

.badge-house-type {
    background-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    font-weight: 500;
    font-size: 0.75rem;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.bg-white-10 {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(6px);
}

.banner-meta-grid {
    width: 100%;
    gap: 0.5rem;
}

.meta-chip {
    transition: transform 0.15s ease, background 0.15s ease;
    min-width: 0;
    border-radius: 12px;
}

/* On phones each chip gets its own full-width row so long authority names are readable */
.meta-chip-value {
    font-size: 0.95rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

@media (min-width: 768px) {
    .banner-meta-grid {
        width: auto;
        max-width: 55%;
    }

    .meta-chip {
        flex: 0 1 auto;
        border-radius: 50rem;
    }

    .meta-chip-value {
        font-size: 0.85rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.min-w-0 {
    min-width: 0;
}

.meta-chip:hover {
    background: rgba(255, 255, 255, 0.2);
}

.text-micro {
    font-size: 0.65rem;
    letter-spacing: 0.5px;
    line-height: 1;
}

.text-sm {
    font-size: 0.85rem;
    line-height: 1.2;
}

.text-white-75 {
    color: rgba(255, 255, 255, 0.85) !important;
}

.text-white-50 {
    color: rgba(255, 255, 255, 0.65) !important;
}

.z-index-1 {
    z-index: 1;
}

.area-group-card {
    transition: all 0.2s ease-in-out;
}

.inspection-card {
    border-radius: 6px;
    transition: box-shadow 0.2s ease-in-out;
}

.inspection-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.add-inspection {
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    background-color: #f8fafc;
    border: 2px dashed #cbd5e1 !important;
}

.add-inspection:hover {
    background-color: #eef2f7 !important;
    border-color: #4f46e5 !important;
    transform: translateY(-1px);
}
</style>
